import { useMemo } from "react";
import {
  Trophy,
  Rocket,
  Globe2,
  Sparkles,
  ShieldCheck,
  CalendarClock,
  Layers,
  Info,
} from "lucide-react";
import {
  buildCompetitorIntelligence,
  platformLabel,
} from "@/utils/competitorAnalysis";

const toneClasses = {
  green: {
    text: "text-green-600 dark:text-green-400",
    bg: "bg-green-500/10",
    bar: "bg-green-500",
    ring: "border-green-500/40",
    badge: "bg-green-500 text-black",
  },
  yellow: {
    text: "text-yellow-600 dark:text-yellow-500",
    bg: "bg-yellow-500/10",
    bar: "bg-yellow-500",
    ring: "border-yellow-500/40",
    badge: "bg-yellow-500 text-black",
  },
  orange: {
    text: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-500/10",
    bar: "bg-orange-500",
    ring: "border-orange-500/40",
    badge: "bg-orange-500 text-black",
  },
  red: {
    text: "text-red-600 dark:text-red-400",
    bg: "bg-red-500/10",
    bar: "bg-red-500",
    ring: "border-red-500/40",
    badge: "bg-red-500 text-white",
  },
  gray: {
    text: "text-zinc-500 dark:text-zinc-400",
    bg: "bg-zinc-500/10",
    bar: "bg-zinc-400",
    ring: "border-zinc-500/40",
    badge: "bg-zinc-800 text-white",
  },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

export default function CompetitorIntelligence({ ad }) {
  const intel = useMemo(() => (ad ? buildCompetitorIntelligence(ad) : null), [ad]);
  if (!ad || !intel) return null;

  const wt = toneClasses[intel.winningTone] || toneClasses.gray;
  const ct = toneClasses[intel.campaignTone] || toneClasses.gray;

  return (
    <div
      data-testid="competitor-intelligence"
      className="mt-10 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950"
    >
      {/* Header */}
      <div className="p-6 md:p-8 border-b border-zinc-200 dark:border-zinc-800 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            // Competitor intelligence
          </p>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-zinc-900 dark:text-white tracking-tight">
            Is this ad quietly winning?
          </h3>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 max-w-lg">
            Rule-based teardown pulling from the Meta Ad Library: how long
            it&apos;s been live, where it runs, and how likely it&apos;s a
            profitable creative worth swiping.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 border border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest">
            <ShieldCheck className="w-3 h-3" />
            100% Rule-based · No AI credits used
          </div>
        </div>
      </div>

      {/* 3-card grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-b border-zinc-200 dark:border-zinc-800">
        {/* Card 1 · Winning Score */}
        <div
          data-testid="ci-card-winning-score"
          className="p-6 md:p-8 lg:border-r border-zinc-200 dark:border-zinc-800"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500">
              <Trophy className="w-3.5 h-3.5" />
              Winning ad score
            </div>
            <span
              data-testid="ci-winning-badge"
              className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 ${wt.badge}`}
            >
              {intel.winningBadge}
            </span>
          </div>

          <div className="flex items-baseline gap-2">
            <span
              data-testid="ci-winning-score"
              className={`font-display font-extrabold text-6xl md:text-7xl tracking-tight ${wt.text}`}
            >
              {intel.winningScore}
            </span>
            <span className="font-display font-bold text-2xl text-zinc-400 dark:text-zinc-600">
              /100
            </span>
          </div>

          {/* Progress bar */}
          <div className="mt-4 h-2 bg-zinc-100 dark:bg-zinc-900 relative overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${wt.bar} transition-all`}
              style={{ width: `${intel.winningScore}%` }}
            />
          </div>

          {/* Reasons */}
          <ul className="mt-6 space-y-2">
            {intel.winningReasons.map((r, i) => (
              <li
                key={r.text}
                data-testid={`ci-winning-reason-${i}`}
                className={`flex items-start gap-2 text-sm ${r.ok ? "text-zinc-700 dark:text-zinc-300" : "text-zinc-500"}`}
              >
                <span className={r.ok ? "text-green-500" : "text-zinc-400"}>
                  {r.ok ? "✓" : "×"}
                </span>
                <span>{r.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Card 2 · Campaign Stage */}
        <div
          data-testid="ci-card-campaign-stage"
          className="p-6 md:p-8 lg:border-r border-t lg:border-t-0 border-zinc-200 dark:border-zinc-800"
        >
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
            <Rocket className="w-3.5 h-3.5" />
            Campaign stage
          </div>

          <div className="flex items-center gap-3">
            <span className="text-4xl" aria-hidden>
              {intel.campaignIcon}
            </span>
            <div>
              <div
                data-testid="ci-campaign-stage"
                className={`font-display font-extrabold text-3xl leading-none tracking-tight ${ct.text}`}
              >
                {intel.campaignStage}
              </div>
            </div>
          </div>

          <dl className="mt-6 space-y-3 text-sm">
            <StatRow label="Started" value={formatDate(intel.startedIso)} testId="ci-started" />
            <StatRow
              label="Running"
              value={intel.runningDays !== null ? `${intel.runningDays} days` : "Unknown"}
              testId="ci-days"
            />
            <StatRow
              label="Status"
              value={
                <span
                  data-testid="ci-status"
                  className={`inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest ${
                    intel.isActive
                      ? "text-green-600 dark:text-green-400"
                      : "text-zinc-500"
                  }`}
                >
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${
                      intel.isActive ? "bg-green-500 animate-glow-pulse" : "bg-zinc-400"
                    }`}
                  />
                  {intel.statusLabel}
                </span>
              }
            />
          </dl>
        </div>

        {/* Card 3 · Scale Indicator */}
        <div
          data-testid="ci-card-scale"
          className="p-6 md:p-8 border-t lg:border-t-0 border-zinc-200 dark:border-zinc-800"
        >
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
            <Globe2 className="w-3.5 h-3.5" />
            Scale indicator
          </div>

          <div className="flex items-center gap-3">
            <span className="text-4xl" aria-hidden>
              {intel.scaleIcon}
            </span>
            <div
              data-testid="ci-scale-class"
              className="font-display font-extrabold text-3xl leading-none tracking-tight text-zinc-900 dark:text-white"
            >
              {intel.scaleIndicator}
            </div>
          </div>

          <dl className="mt-6 space-y-3 text-sm">
            <StatRow
              label="Countries"
              value={
                intel.countriesKnown ? (
                  <span data-testid="ci-country-count" className="font-mono">
                    {intel.countryCount}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-zinc-500 text-xs">
                    <Info className="w-3 h-3" /> Not exposed by Meta for this ad
                  </span>
                )
              }
              testId="ci-countries-row"
            />
            <div>
              <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                <Layers className="w-3 h-3" />
                Platforms
              </dt>
              <dd className="flex flex-wrap gap-1.5" data-testid="ci-platforms">
                {intel.platforms.length ? (
                  intel.platforms.map((p) => (
                    <span
                      key={p}
                      data-testid={`ci-platform-${p.toLowerCase()}`}
                      className="text-[10px] font-mono font-bold uppercase tracking-widest bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-2 py-1 text-zinc-800 dark:text-zinc-200"
                    >
                      {platformLabel(p)}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-zinc-500">Unknown</span>
                )}
              </dd>
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Tier · {intel.platformClass}
              </div>
            </div>
            <StatRow
              label="Confidence"
              value={
                <span
                  data-testid="ci-confidence"
                  className={`text-xs font-mono uppercase tracking-widest ${
                    intel.confidence === "High"
                      ? "text-green-600 dark:text-green-400"
                      : intel.confidence === "Medium"
                        ? "text-yellow-600 dark:text-yellow-500"
                        : "text-zinc-500"
                  }`}
                >
                  {intel.confidence}
                </span>
              }
            />
          </dl>
        </div>
      </div>

      {/* Summary card */}
      <div
        data-testid="ci-summary"
        className="p-6 md:p-8 bg-zinc-50 dark:bg-zinc-900/40"
      >
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-yellow-500 mb-3">
          <CalendarClock className="w-3.5 h-3.5" />
          Competitor summary
        </div>
        <p className="text-base md:text-lg text-zinc-800 dark:text-zinc-200 leading-relaxed">
          {intel.summary}
        </p>
      </div>
    </div>
  );
}

function StatRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className="text-sm font-semibold text-zinc-900 dark:text-white text-right"
      >
        {value}
      </dd>
    </div>
  );
}
